
import { Component, computed, inject, input, OnInit, signal, DestroyRef } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { switchMap, catchError, of, filter, tap, Observable } from 'rxjs';
import { IntegrationEntityName, IntegrationService } from '@lb/api/mylb/services/integration.service';
import { IIntegrationDetails, IIntegrationApplication } from '@lb/api/mylb/interfaces';
import { LoaderComponent } from '@lb/components/loader/loader.component';
import { IntegrationDialogService } from '@lb/components/integration-dialog/integration-dialog.service';
import { MDIconComponent } from '@melodic-ui/components';
import { APP_CONFIG, IAppSettings } from '@lb/config/interfaces/iapp-settings.interface';
import { AccountStateService } from '@lb/state/account-state.service';

export type ExternalLinksMode = 'compact' | 'labeled';

interface IExternalLinkConfig {
	application: string;
	entityTypes: string[];
	baseUrl?: string;
	urlTemplate?: string;
	entityConfigs?: IEntitySpecificConfig[];
	logoPath: string;
	displayName: string;
	visibilityRules: ILinkVisibilityRule[];
	order: number;
}

interface IEntitySpecificConfig {
	entityType: string;
	baseUrl: string;
	urlTemplate: string;
	logoPath?: string;
}

interface ILinkVisibilityRule {
	entityType: string;
	requiredEntitlements?: string[];
	allowedForAll?: boolean;
}

interface IUrlBuilderContext {
	entityId?: number;
	entityGuid?: string;
	contactId?: string;
	dealId?: string;
	teamId?: string;
	employeeId?: string;
	customerId?: string;
	applicationValue?: string;
	groupId?: string;
	userId?: string;
	channelUrl?: string;
	channelId?: string;
	encodedChannelName?: string;
	tenantId?: string;
	[key: string]: string | number | undefined;
}

export interface IGeneratedLink {
	key: string;
	application: string;
	displayName: string;
	url: string;
	logoPath: string;
	visible: boolean;
	order: number;
}

@Component({
	selector: 'lb-external-links',
	standalone: true,
	imports: [LoaderComponent, MDIconComponent],
	templateUrl: './external-links.component.html',
	styleUrl: './external-links.component.scss'
})
export class ExternalLinksComponent implements OnInit {
	private _integrationService = inject(IntegrationService);
	private _integrationDialogService = inject(IntegrationDialogService);
	private _accountStateService = inject(AccountStateService);
	private _destroyRef = inject(DestroyRef);
	private _appSettings = inject<IAppSettings>(APP_CONFIG);

	private readonly linkConfigurations: IExternalLinkConfig[] = [
		{
			application: 'HubSpot',
			entityTypes: ['Enrollment', 'User'],
			logoPath: 'assets/images/hubspot.png',
			displayName: 'HubSpot',
			entityConfigs: [
				{
					entityType: 'Enrollment',
					baseUrl: this._appSettings.externalLinks?.hubspot?.baseUrl || 'https://app.hubspot.com/',
					urlTemplate: 'record/0-3/{dealId}'
				},
				{
					entityType: 'User',
					baseUrl: this._appSettings.externalLinks?.hubspot?.baseUrl || 'https://app.hubspot.com/',
					urlTemplate: 'record/0-1/{contactId}'
				}
			],
			visibilityRules: [
				{
					entityType: 'Enrollment',
					requiredEntitlements: ['Admin:App:Access']
				},
				{
					entityType: 'User',
					requiredEntitlements: ['Admin:App:Access']
				}
			],
			order: 1
		},
		{
			application: 'Azure',
			entityTypes: ['Enrollment'],
			baseUrl: 'https://outlook.office.com/',
			urlTemplate: 'mail/inbox/',
			logoPath: 'assets/images/outlook.svg',
			displayName: 'Outlook',
			entityConfigs: [],
			visibilityRules: [
				{
					entityType: 'Enrollment',
					allowedForAll: true
				}
			],
			order: 2
		},
		{
			application: 'Azure',
			entityTypes: ['Enrollment'],
			logoPath: 'assets/images/teams.svg',
			displayName: 'Teams',
			entityConfigs: [
				{
					entityType: 'Enrollment',
					baseUrl: '',
					urlTemplate: '{channelUrl}'
				}
			],
			visibilityRules: [
				{
					entityType: 'Enrollment',
					allowedForAll: true
				}
			],
			order: 3
		},
		{
			application: 'Azure',
			entityTypes: ['Enrollment', 'User'],
			logoPath: 'assets/images/azure.svg',
			displayName: 'Azure',
			entityConfigs: [
				{
					entityType: 'Enrollment',
					baseUrl: 'https://admin.teams.microsoft.com/teams/',
					urlTemplate: 'manage/{groupId}/users'
				},
				{
					entityType: 'User',
					baseUrl: 'https://entra.microsoft.com/',
					urlTemplate: '#view/Microsoft_AAD_UsersAndTenants/UserProfileMenuBlade/~/overview/userId/{userId}/hidePreviewBanner~/true'
				}
			],
			visibilityRules: [
				{
					entityType: 'Enrollment',
					requiredEntitlements: ['Admin:App:Access']
				},
				{
					entityType: 'User',
					requiredEntitlements: ['Admin:App:Access']
				}
			],
			order: 4
		},
		{
			application: 'Bamboo',
			entityTypes: ['User'],
			baseUrl: 'https://languagebird.bamboohr.com',
			urlTemplate: '/employees/employee.php?id={employeeId}&page=2115',
			logoPath: 'assets/images/bamboo-hr.png',
			displayName: 'BambooHR',
			visibilityRules: [
				{
					entityType: 'User',
					requiredEntitlements: ['Admin:App:Access']
				}
			],
			order: 5
		}
	];

	// Inputs — give one of the two. Prefer entityId: the backend looks the correlation GUID up in the
	// Integration hub, whereas a GUID copied onto a MyLB row (Core.Users.UserGUID) can be stale and send the
	// links to someone else's accounts. entityGuid remains for GUIDs that have no MyLB entity behind them,
	// such as a merged user's parked integrations.
	entityGuid = input<string | null>(null);
	entityId = input<number | null>(null);
	entityType = input<IntegrationEntityName>('Enrollment');
	mode = input<ExternalLinksMode>('compact');

	// State
	integrationDetails = signal<IIntegrationDetails | null>(null);
	isLoading = signal<boolean>(false);
	error = signal<string | null>(null);

	constructor() {
		// Initialization moved to ngOnInit
	}

	// Get current user entitlements for visibility checks
	currentUser = this._accountStateService.user;
	userEntitlements = computed(() => this.currentUser()?.entitlements?.map((e) => e.entitlementName) || []);

	showEditExternalLinks = computed(() => {
		const entitlements = this.userEntitlements();
		// The dialog edits by GUID, so it needs one — an entity with no hub mapping has nothing to edit.
		return entitlements.includes('Admin:Integration:Edit') && !!this.resolvedGuid();
	});

	/** The GUID the loaded details belong to — what the integration dialog edits, however the entity was addressed. */
	private readonly resolvedGuid = computed(() => this.integrationDetails()?.guid ?? this.entityGuid());

	// Observables derived from signals (declared after computed to ensure init order)
	private readonly source$ = toObservable(
		computed(() => ({ id: this.entityId(), guid: this.entityGuid(), entityType: this.entityType() }), {
			equal: (a, b) => a.id === b.id && a.guid === b.guid && a.entityType === b.entityType
		})
	);

	// Computed external links with visibility and URL generation
	externalLinks = computed<IGeneratedLink[]>(() => {
		const details = this.integrationDetails();
		const entityType = this.entityType();
		const entitlements = this.userEntitlements();

		if (!details?.applications || !entityType) return [];

		// Get link configurations for this entity type
		const configs = this.getConfigurationsForEntityType(entityType);

		return configs
			.map((config) => {
				// Check visibility first
				const visible = this.isLinkVisible(config.visibilityRules, entityType, entitlements);

				if (!visible) {
					return null; // Skip invisible links
				}

				// Find matching integration data
				const integrationAppName = config.application.toLowerCase();
				const appData = details.applications?.find((app) => app.applicationName.toLowerCase() === integrationAppName);

				if (!appData) {
					return null; // No integration data available
				}

				// Build URL context from integration data
				const context: IUrlBuilderContext = {
					entityGuid: details.guid,
					applicationValue: appData.applicationValue
				};

				if (appData.metadata) {
					Object.entries(appData.metadata).forEach(([key, value]) => {
						context[key] = value;
					});
				}

				// Add application-specific context mapping
				this.addApplicationSpecificContext(config.application, appData, context, entityType);

				// Build the URL
				let url: string | null;

				// Special handling for applications that might store full URLs
				if (config.application.toLowerCase() === 'hubspot' && appData.applicationValue?.startsWith('http')) {
					// HubSpot stores full URLs, use as-is
					url = appData.applicationValue;
				} else {
					// Use URL builder for template-based URLs with entity type
					url = this.buildUrlSafe(config, context, entityType);
				}

				if (!url) {
					return null; // Skip if URL can't be built
				}

				// Get entity-specific logo path if available, otherwise use default
				const entityConfig = config.entityConfigs?.find((ec) => ec.entityType === entityType);
				const logoPath = entityConfig?.logoPath || config.logoPath;

				return {
					key: `${config.application}-${config.order}`,
					application: config.application,
					displayName: config.displayName,
					url,
					logoPath,
					visible: true,
					order: config.order
				} as IGeneratedLink;
			})
			.filter((link): link is IGeneratedLink => link !== null)
			.sort((a, b) => a.order - b.order);
	});

	ngOnInit(): void {
		// Load data only when we have an id or a guid AND external links should be shown
		this.source$
			.pipe(
				filter((source) => !!source.id || !!source.guid),
				tap(() => {
					this.isLoading.set(true);
					this.error.set(null);
				}),
				switchMap((source) => this._load(source.id, source.guid, source.entityType)),
				tap(() => this.isLoading.set(false)),
				takeUntilDestroyed(this._destroyRef)
			)
			.subscribe((details) => {
				this.integrationDetails.set(details);
			});
	}

	/** An entity id wins over a guid; a 404 just means the entity has no hub mapping, so no links. */
	private _load(id: number | null, guid: string | null, entityType: IntegrationEntityName): Observable<IIntegrationDetails | null> {
		const request$ = id
			? this._integrationService.getByEntityId(entityType, id, { includeMetadata: true })
			: this._integrationService.getByGuid(guid!, { includeMetadata: true });

		return request$.pipe(
			catchError((error) => {
				if (error?.status !== 404) {
					console.error('Error loading integration details:', error);
				}
				this.error.set('');
				return of(null);
			})
		);
	}

	private addApplicationSpecificContext(application: string, appData: IIntegrationApplication, context: IUrlBuilderContext, entityType: string): void {
		// Map application values to specific context keys based on application type
		switch (application.toLowerCase()) {
			case 'hubspot':
				if (entityType === 'Enrollment') {
					context.dealId = appData.applicationValue;
				} else if (entityType === 'User') {
					context.contactId = appData.applicationValue;
				}
				break;
			case 'azure':
				if (entityType === 'Enrollment') {
					context.groupId = appData.applicationValue;
					const metadata = appData.metadata ?? {};
					if (metadata['channelId']) {
						context.channelId = metadata['channelId'];
					}
					if (metadata['tenantId']) {
						context.tenantId = metadata['tenantId'];
					}
					if (context.channelId && context.groupId) {
						const tenant = context.tenantId || '349f1192-894a-4089-aba1-145539e2f49b';
						context.channelUrl = `https://teams.microsoft.com/l/team/${context.channelId}/conversations?groupId=${context.groupId}&tenantId=${tenant}`;
					} else {
						context.channelUrl = 'https://teams.microsoft.com/?web=1';
					}
				} else if (entityType === 'User') {
					context.userId = appData.applicationValue;
				}
				break;

			case 'bamboo':
				context.employeeId = appData.applicationValue;
				break;
		}
	}

	openExternalLink(link: IGeneratedLink): void {
		if (link.url) {
			window.open(link.url, '_blank');
		}
	}

	openIntegrationDialog(): void {
		const guid = this.resolvedGuid();
		if (!guid) {
			return;
		}

		const dialogRef = this._integrationDialogService.openIntegrationDialog(guid, {
			entityType: this.entityType(),
			readonly: false
		});

		// Update details after dialog closes; if result present, use it, otherwise refetch
		dialogRef.afterClosed.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((result) => {
			if (result && typeof result === 'object' && 'guid' in result) {
				this.integrationDetails.set(result as IIntegrationDetails);
			} else {
				// If nothing returned, trigger a reload if links are visible
				if (this.showEditExternalLinks()) {
					this.isLoading.set(true);
					this._load(this.entityId(), guid, this.entityType())
						.pipe(takeUntilDestroyed(this._destroyRef))
						.subscribe((details) => {
							this.integrationDetails.set(details);
							this.isLoading.set(false);
						});
				}
			}
		});
	}

	private getConfigurationsForEntityType(entityType: string): IExternalLinkConfig[] {
		return this.linkConfigurations.filter((config) => config.entityTypes.includes(entityType)).sort((a, b) => a.order - b.order);
	}

	private isLinkVisible(visibilityRules: ILinkVisibilityRule[], entityType: string, userEntitlements?: string[]): boolean {
		// Find the rule for this entity type
		const rule = visibilityRules.find((r) => r.entityType === entityType);

		if (!rule) {
			return false; // No rule found, hide by default
		}

		// If allowed for all, show it
		if (rule.allowedForAll) {
			return true;
		}

		// If specific entitlements are required, check if user has one of them
		if (rule.requiredEntitlements && rule.requiredEntitlements.length > 0) {
			if (!userEntitlements || userEntitlements.length === 0) {
				return false;
			}
			return rule.requiredEntitlements.some((requiredEntitlement) => userEntitlements.includes(requiredEntitlement));
		}

		// Default to hidden if no explicit rules match
		return false;
	}

	// ===== Inlined service methods (previously in UrlBuilderService) =====

	private buildUrlSafe(config: IExternalLinkConfig, context: IUrlBuilderContext, entityType?: string): string | null {
		const validation = this.validateContext(config, context, entityType);

		if (!validation.isValid) {
			console.debug(`Cannot build URL for ${config.application}: missing ${validation.missingPlaceholders.join(', ')}`);
			return null;
		}

		return this.buildUrl(config, context, entityType);
	}

	private buildUrl(config: IExternalLinkConfig, context: IUrlBuilderContext, entityType?: string): string {
		// Get entity-specific configuration if available
		const entityConfig = this.getEntityConfig(config, entityType);

		let url = entityConfig.baseUrl + entityConfig.urlTemplate;

		// Replace all placeholders in the URL template
		Object.entries(context).forEach(([key, value]) => {
			if (value !== undefined && value !== null) {
				const placeholder = `{${key}}`;
				url = url.replace(placeholder, String(value));
			}
		});

		// Log warning if any placeholders remain unreplaced
		const remainingPlaceholders = url.match(/\{[^}]+\}/g);
		if (remainingPlaceholders) {
			console.warn(`URL template has unreplaced placeholders: ${remainingPlaceholders.join(', ')} in ${url}`);
		}

		return url;
	}

	private getEntityConfig(config: IExternalLinkConfig, entityType?: string): { baseUrl: string; urlTemplate: string } {
		// If entity type is specified and entity-specific config exists, use it
		if (entityType && config.entityConfigs) {
			const entitySpecificConfig = config.entityConfigs.find((ec) => ec.entityType === entityType);
			if (entitySpecificConfig) {
				return {
					baseUrl: entitySpecificConfig.baseUrl,
					urlTemplate: entitySpecificConfig.urlTemplate
				};
			}
		}

		// Fall back to default configuration
		if (!config.baseUrl || !config.urlTemplate) {
			throw new Error(`No configuration found for application ${config.application} and entity type ${entityType}`);
		}

		return {
			baseUrl: config.baseUrl,
			urlTemplate: config.urlTemplate
		};
	}

	private validateContext(
		config: IExternalLinkConfig,
		context: IUrlBuilderContext,
		entityType?: string
	): {
		isValid: boolean;
		missingPlaceholders: string[];
	} {
		const entityConfig = this.getEntityConfig(config, entityType);
		const requiredPlaceholders = this.getPlaceholdersFromTemplate(entityConfig.urlTemplate);
		const providedKeys = Object.keys(context).filter((key) => context[key] !== undefined && context[key] !== null && context[key] !== '');

		const missingPlaceholders = requiredPlaceholders.filter((placeholder) => !providedKeys.includes(placeholder));

		return {
			isValid: missingPlaceholders.length === 0,
			missingPlaceholders
		};
	}

	private getPlaceholdersFromTemplate(urlTemplate: string): string[] {
		const matches = urlTemplate.match(/\{([^}]+)\}/g);
		return matches ? matches.map((match) => match.slice(1, -1)) : [];
	}
}
